// File: src/components/modals/InviteAccountModal.jsx
//
// Invite a new ServiceProvider or ServiceProviderAdmin account.
//
// Both roles call adminAPI.inviteAccount({ type, contact, role }):
// - 'admin' role: sees a role selector (ServiceProvider | ServiceProviderAdmin)
// - 'ServiceProviderAdmin' role: role selector hidden, always sends
//   role='ServiceProvider' — also enforced server-side regardless of input.

import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Mail, Send, CheckCircle, UserCog, GraduationCap } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from '../../utils/toast';

const AUTH0_ROLES_CLAIM = 'https://booking-slot.com/roles';

const InviteAccountModal = ({ onClose, onInvited, defaultRole = 'ServiceProvider' }) => {
  const { user, getAccessTokenSilently } = useAuth0();

  const roles = user?.[AUTH0_ROLES_CLAIM] || [];
  const isSystemAdmin = roles.includes('admin');

  const [email,    setEmail]    = useState('');
  const [role,     setRole]     = useState(defaultRole);
  const [error,    setError]    = useState('');
  const [isSending, setIsSending] = useState(false);
  const [success,  setSuccess]  = useState('');

  const validate = () => {
    if (!email.trim()) { setError('Email address is required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSend = async () => {
    if (!validate()) return;
    setIsSending(true);
    setError('');
    setSuccess('');

    try {
      // Same endpoint for both 'admin' and 'ServiceProviderAdmin'.
      // For ServiceProviderAdmin, `role` is fixed to 'ServiceProvider'
      // (selector is hidden — see below); the backend also enforces this
      // server-side regardless of what's sent.
      const res = await adminAPI.inviteAccount(
        { type: 'email', contact: email.trim(), role },
        getAccessTokenSilently
      );

      if (!res.success) throw new Error(res.error || 'Failed to send invitation');

      setSuccess(`Invitation sent to ${email.trim()}`);
      toast.success(`Invitation sent to ${email.trim()}`);
      if (onInvited) onInvited();
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="template-modal-overlay">
      <div className="template-modal">
        <div className="modal-header">
          <h3>Invite Account</h3>
          <button className="modal-close" onClick={onClose} disabled={isSending}>×</button>
        </div>

        <div className="template-list">

          {success && (
            <div style={{
              padding: '1rem', backgroundColor: '#d1fae5', border: '1px solid #10b981',
              borderRadius: '0.5rem', marginBottom: '1rem',
              display: 'flex', alignItems: 'center', gap: '0.75rem'
            }}>
              <CheckCircle size={22} color="#10b981" />
              <span style={{ color: '#065f46', fontWeight: 500, fontSize: '0.9375rem' }}>{success}</span>
            </div>
          )}

          {!success && (
            <>
              {/* Role selector — only for 'admin' (system) role */}
              {isSystemAdmin && (
                <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                  <label className="form-label">Account Type</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>

                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.875rem 1rem', border: '2px solid',
                      borderColor: role === 'ServiceProvider' ? '#2563eb' : '#e5e7eb',
                      borderRadius: '0.5rem', cursor: 'pointer',
                      background: role === 'ServiceProvider' ? '#eff6ff' : '#fff',
                      transition: 'all 0.2s'
                    }}>
                      <input type="radio" name="role" value="ServiceProvider"
                        checked={role === 'ServiceProvider'}
                        onChange={() => setRole('ServiceProvider')}
                        style={{ width: '1.125rem', height: '1.125rem', cursor: 'pointer', accentColor: '#2563eb' }} />
                      <GraduationCap size={20} color="#2563eb" />
                      <div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                          Service Provider
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                          Driving instructor — manages their own students, slots, and templates
                        </div>
                      </div>
                    </label>

                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.875rem 1rem', border: '2px solid',
                      borderColor: role === 'ServiceProviderAdmin' ? '#7c3aed' : '#e5e7eb',
                      borderRadius: '0.5rem', cursor: 'pointer',
                      background: role === 'ServiceProviderAdmin' ? '#f5f3ff' : '#fff',
                      transition: 'all 0.2s'
                    }}>
                      <input type="radio" name="role" value="ServiceProviderAdmin"
                        checked={role === 'ServiceProviderAdmin'}
                        onChange={() => setRole('ServiceProviderAdmin')}
                        style={{ width: '1.125rem', height: '1.125rem', cursor: 'pointer', accentColor: '#7c3aed' }} />
                      <UserCog size={20} color="#7c3aed" />
                      <div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                          Service Provider Admin
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                          Manages instructor accounts and can act on their behalf
                        </div>
                      </div>
                    </label>

                  </div>
                </div>
              )}

              {/* Email input */}
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                  border: '1.5px solid #e5e7eb', borderRadius: '0.5rem',
                  padding: '0 0.65rem', background: '#fff' }}>
                  <Mail size={15} color="#9ca3af" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="instructor@example.com"
                    disabled={isSending}
                    style={{
                      flex: 1, border: 'none', outline: 'none',
                      padding: '0.6rem 0', fontSize: '0.9rem',
                      fontFamily: 'inherit', background: 'transparent'
                    }}
                  />
                </div>
                {error && (
                  <div style={{
                    marginTop: '0.5rem', padding: '0.6rem 0.85rem',
                    background: '#fef2f2', border: '1px solid #fca5a5',
                    borderRadius: '0.4rem', fontSize: '0.82rem', color: '#991b1b'
                  }}>
                    ⚠️ {error}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="form-actions">
                <button type="button" onClick={onClose} disabled={isSending} className="btn-cancel">
                  Cancel
                </button>
                <button type="button" onClick={handleSend} disabled={isSending} className="btn-save"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Send size={16} />
                  {isSending ? 'Sending…' : 'Send Invitation'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteAccountModal;
